/**
 * <apple-pass-schematic> — a SCHEMATIC of the Apple Wallet `eventTicket` a
 * ticket produces, mirroring the slot-for-slot composition of
 * `buildTicketPassProps` (firebase/src/settings/appleWallet.api.ts)
 * in one order top to bottom: the mark, the strip, DATE | VENUE, TICKET |
 * TICKET NO., the barcode — the same order the Google schematic beside it
 * draws, because both passes carry the issuer's mark in their own head.
 *
 * EVERY ROW IS THE CARD'S ROW, IN THE CARD'S WORDS. The labels resolve through
 * `documents.ticket.*` — the very keys the stencil card reads — and the date
 * through `formatDateRange` at the card's own options (`month: "long"`, the
 * venue's zone, the buyer locale's tag), which is the same call the card makes
 * and the same call the composer makes. A pass-side formatter or a pass-side
 * label set is a second expression of one fact, so neither exists.
 *
 * WHERE THE EVENT NAME SITS IS ONE DECISION WITH TWO ENDS, and the frame makes
 * it the way `buildTicketPassProps` does. Apple draws the primary field OVER
 * the strip, so a strip that already spells the name in the event's own
 * wordmark and a primary field repeating it as text would collide and say it
 * twice: when a band is supplied the band IS the name and no text row repeats
 * it; when none is, the name takes the primary field with no label, because the
 * card's wordmark carries none.
 *
 * THE MARK DRAWS IN THE HEADER, TOP-LEFT — the one place Apple draws a mark,
 * one region above the strip — AND NOTHING RENDERS BESIDE IT. The mark IS the
 * brand, so a name next to it says the same thing twice, and `headerFields` is
 * a LABELLED pair whose caption would drag the card's own "Produced by" from
 * the card's foot into the pass's head. `headerFields` stays empty; `logoText`
 * carries the name alone, and only when no mark resolves — which is the
 * fallback this frame draws — the Google schematic's own shape, in Apple's
 * `labelColor` rather than Google's derived muted ink, because each pass's
 * caption tone is that pass's own.
 *
 * WHICH FILE IS THE MARK FOLLOWS THE GROUND'S INK POLARITY, derived through the
 * same `walletInkPolarity` the composer's own ladder is ordered by rather than
 * named here a second time: on a dark ground the derived light
 * `wallet-logo.png` first and the operator's own after, on a light ground the
 * operator's own only, because the derived mark is whitened by contract and
 * cannot be seen there. `editorial` and `strata` resolve a `light` ink polarity
 * — a dark ground — while `stencil`'s pinned `#f5701a` resolves `dark`, a light
 * ground on which the derived mark measures 2.91:1 against a dark wordmark's
 * 7.22:1.
 *
 * THE MARK IS SIZED AT APPLE'S OWN MEASURE, not at the Google frame's head
 * height. Apple draws `logo.png` at up to 160 × 50 pt on a ~375 pt-wide pass,
 * and this 340 px card is that pass at ≈0.907 px/pt, so the faithful box is
 * 45 × 145 px — `h-11`, the scale's own 44 px, and a 145 px cap, with the mark
 * letterboxed inside whichever bound its own aspect meets first. The two
 * frames' heads therefore differ in size on purpose: each is drawn at its own
 * vendor's measure, and matching them would draw the Apple mark at a quarter of
 * the area Wallet actually gives it.
 *
 * WHO OWNS WHAT, because a schematic promising a layout Wallet will not produce
 * is worse than none. We hand Apple strings, four colours and two bundled
 * images; Apple draws the row grid, uppercases the labels, owns the type scale,
 * and owns the barcode's white plate AND its centred position — which is why
 * the one centred element on a surface whose whole discipline is left-hanging
 * is the one element we do not author. The secondary and auxiliary rows hang on
 * their own leading edge, which is the `PKTextAlignmentLeft` the composer ships
 * on each of them; the primary and back rows take Apple's own alignment,
 * because Apple documents `textAlignment` as invalid on those two.
 *
 * TWO FIELD TIERS, TWO VALUE SCALES. Apple renders an auxiliary value smaller
 * than a secondary one, so the logistics pair (DATE | VENUE) hangs at
 * `title/sm` and the credential pair (TICKET | TICKET NO.) at `body/sm`; both
 * tiers keep one label scale, because Apple's own labels do.
 *
 * ONE VERTICAL RHYTHM, SHARED WITH THE GOOGLE SCHEMATIC. Three distances and no
 * others: `gap-1` inside a label/value cluster, `gap-3` between cells, `gap-6`
 * between regions and around the card. The strip's own height is the single
 * distance the two surfaces cannot share, because only Apple has a strip.
 *
 * NO BACK SHEET. `backFields` ships empty, so Apple renders no back section and
 * the venue's postal address — a fact the card never shows — is not sent at
 * all; nothing is listed under the frame either.
 *
 * THE STRIP IS THE CARD'S OWN TOP, and `<wallet-panel>` draws it from
 * `walletPanelTreatment` rather than from a gradient written here: that is the
 * same decision the signed archive's own strip is produced from, so this frame
 * shows the strip the buyer's pass actually bundles. A composition IS the
 * card's hero region at the card's own typographic scale — the card's ground
 * under the card's ink, so it composites onto the pass ground with no band edge
 * of its own — while a painted treatment carries no ink at all, since Apple
 * overlays the primary field on it at a vertical position of its choosing.
 *
 * BRAND IDENTITY IS LIVE, THE EVENT AND TICKET ARE THE SAMPLE: the mark
 * (`walletPassMarkUri`), the name it falls back to (`brandName`) and the
 * deployment's own seed (`brandSeedColor`) are read live and never fixtured,
 * because the composer reads them live too. The event, venue, schedule, ticket
 * type and display id stay the preview sample (preview.state.ts): a preview has
 * no real order behind it.
 *
 * Grounds and inks are painted inline — a wallet pass is a document, so its
 * colours are its own and never the host theme's. The pass's own strings
 * resolve through `translate(locale, "documents.ticket.*")` at the sample's
 * buyer locale; the annotation below the frame is admin chrome in the admin's
 * own UI language.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
// Side-effect: registers <schmancy-qr>.
import "@mhmo91/schmancy/qr";
import { formatDateRange } from "@mhmo91/rx";
import { translate } from "@momo/i18n";
import {
	WALLET_STRIP_HEIGHT,
	WALLET_STRIP_WIDTH,
	walletPanelTreatment,
	walletPassPalette,
} from "@momo/ticket-template/wallet";
import {
	DEFAULT_TICKET_TEMPLATE_VARIANT,
	type TicketTemplateVariant,
} from "@momo/types";
import { html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../i18n/localize.ts";
import {
	brandName,
	brandSeedColor,
	walletPassMarkUri,
} from "../workspace/workspace.state.ts";
import { ticketPreviewProps } from "./preview.state.ts";
// Side-effect: registers <wallet-panel>.
import "./wallet-panel.view.ts";

@schmancyElement("apple-pass-schematic")
export class ApplePassSchematic extends LocalizeMixin(SchmancyElement) {
	@property({ attribute: false })
	variant: TicketTemplateVariant = DEFAULT_TICKET_TEMPLATE_VARIANT;

	override render(): TemplateResult {
		const palette = walletPassPalette(brandSeedColor.get(), this.variant);
		const { locale, event, ticket } = ticketPreviewProps.get();
		// The one boolean both ends of the name's placement read, so the two
		// cannot fall out of step: a band spells the name in the event's own
		// wordmark, and the primary field over the strip carries it as text
		// exactly when the band does not.
		const bandSpellsTheName =
			walletPanelTreatment(brandSeedColor.get(), this.variant).kind ===
			"composition";
		const field = (
			label: string,
			value: string,
			valueType: "title" | "body",
			// Only the schedule cell's value is a RANGE, and at this column width it
			// wraps: left unbalanced it breaks after the en dash, stranding it at the
			// line end where it reads as a truncated string rather than a span.
			// Balanced, the date takes one line and both times stay together on the
			// next. The other three values are single terms with nothing to strand.
			balanceValue = false,
		) => html`
			<div class="grid content-start gap-1">
				<schmancy-typography
					type="label"
					token="xs"
					transform="uppercase"
					class="tracking-widest"
					style="color:${palette.label};"
				>
					${label}
				</schmancy-typography>
				<schmancy-typography
					.type=${valueType}
					token="sm"
					class=${this.classMap({ "text-balance": balanceValue })}
				>
					${value}
				</schmancy-typography>
			</div>
		`;

		return html`
			<div class="grid w-max max-w-full gap-6 p-6">
				<div
					class="grid w-[340px] max-w-full gap-6 overflow-hidden rounded-2xl py-6 shadow-lg"
					style="background-color:${palette.ground};color:${palette.ink};"
				>
					${when(
						walletPassMarkUri(this.variant),
						(logo) => html`
							<img
								src=${logo}
								alt=${brandName.get()}
								class="ml-4 h-11 w-auto max-w-[145px] justify-self-start object-contain"
							/>
						`,
						() => html`
							<schmancy-typography
								type="label"
								token="sm"
								transform="uppercase"
								class="min-w-0 px-4 tracking-widest"
								style="color:${palette.label};"
							>
								${brandName.get()}
							</schmancy-typography>
						`,
					)}

					<wallet-panel
						.variant=${this.variant}
						.eventName=${event.name}
						.logoUrl=${event.logoUrl ?? null}
						.widthPx=${WALLET_STRIP_WIDTH}
						.heightPx=${WALLET_STRIP_HEIGHT}
					>
						${when(
							!bandSpellsTheName,
							() => html`
								<div class="grid content-center gap-1 px-4">
									<schmancy-typography
										type="headline"
										token="sm"
										weight="bold"
										maxLines="2"
										class="leading-tight"
									>
										${event.name}
									</schmancy-typography>
								</div>
							`,
						)}
					</wallet-panel>

					<div class="grid grid-cols-2 gap-3 px-4">
						${field(
							translate(locale, "documents.ticket.dateLabel"),
							formatDateRange(event.start, event.end, {
								locale: locale === "de" ? "de-DE" : "en-US",
								month: "long",
								timeZone: event.timezone,
							}),
							"title",
							true,
						)}
						${field(
							translate(locale, "documents.ticket.venueLabel"),
							event.venue.name,
							"title",
						)}
						${field(
							translate(locale, "documents.ticket.ticketTypeLabel"),
							ticket.typeName,
							"body",
						)}
						${field(
							translate(locale, "documents.ticket.ticketNumberLabel"),
							ticket.displayId,
							"body",
						)}
					</div>

					<!-- The plate, the black modules and the centring are Apple's own
					     chrome, drawn from the barcode message alone: nothing in
					     pass.json tints or moves them, so this is the one element on
					     the pass we do not author. -->
					<div class="grid justify-center px-4">
						<div class="rounded-lg bg-white p-2.5">
							<schmancy-qr
								.value=${ticket.qrToken}
								.size=${112}
								.moduleColor=${"#000000"}
							></schmancy-qr>
						</div>
					</div>
				</div>

				<schmancy-typography
					type="body"
					token="sm"
					class="w-[340px] max-w-full text-surface-onVariant"
				>
					${this.t("admin.ticketTemplates.walletPreview.appleNote")}
				</schmancy-typography>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"apple-pass-schematic": ApplePassSchematic;
	}
}
